import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { usePointCloudContext } from 'features/pointClouds'
import { Vector3 } from 'three'
import {
  GeckoObservationTicket,
  GeckoSiteObservation,
  ObservationType,
  RolodexEntryPutJson,
  RolodexEntryType,
  TicketStatus,
} from 'types'
import { v4 as uuid4 } from 'uuid'

import { useObservations } from './useObservations'

export const useObservationHandlers = () => {
  const { rolodexPut, rolodexUploadFile, rolodexPatch } = useRolodexEdit()

  const {
    observationPosition,
    setObservationPosition,
    hitPosition,
    editingObservation,
    setEditingObservation,
    site,
    organization,
    activePointCloud,
  } = usePointCloudContext()

  const { observations, forceRefresh } = useObservations()

  const handleEditObservation = (observation: GeckoSiteObservation) => {
    setEditingObservation(observation)

    if (observation.data?.pointCloudCoordinates?.coordinate) {
      const [x, y, z] = observation.data.pointCloudCoordinates.coordinate
      setObservationPosition(new Vector3(x, y, z))
    }
  }
  const handleDeleteObservation = async (observationId: string) => {
    const observationToDelete = observations.find(
      (ind) => ind.id === observationId
    )
    if (!observationToDelete) {
      console.error('Cannot delete observation: not found in state', {
        observationId,
      })
      return
    }

    try {
      setEditingObservation(null)
      setObservationPosition(null)

      await rolodexPatch([
        {
          id: observationId,
          data: {
            alive: false,
          },
        },
      ])
      await forceRefresh()
    } catch (error) {
      console.error('Error deleting observation:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        observationId,
      })
      await forceRefresh()
    }
  }

  const handleSaveObservation = async (
    observation: Partial<GeckoSiteObservation>,
    images?: string[]
  ) => {
    const position = hitPosition || observationPosition

    let observationId: string
    if (editingObservation?.id) {
      observationId = editingObservation.id
    } else {
      do {
        observationId = 'obs-' + uuid4().slice(0, 8)
      } while (observations.some((obs) => obs.id === observationId))
    }

    const newObservation: Partial<GeckoSiteObservation> = {
      id: observationId,
      type: RolodexEntryType.OBSERVATION,
      data: {
        id: observationId,
        status: observation.data?.status || TicketStatus.NEW,
        kind: 'point',
        type: observation.data?.type || ObservationType.VISUAL,
        name: observation.data?.name,
        description: observation.data?.description,
        area_id: observation.data?.area_id,
        alive: true,
        severity: observation.data?.severity,
        department: observation.data?.department,
        wo_number: observation.data?.wo_number,
        component: observation.data?.component,
        condition: observation.data?.condition,
        equipment: observation.data?.equipment,
        date_found: observation.data?.date_found,
        notification_number: observation.data?.notification_number,
        original_created_date: observation.data?.original_created_date,

        pointCloudCoordinates: position
          ? {
              coordinate: [position.x, position.y, position.z],
            }
          : undefined,
        maxarCoordinates: {
          coordinate: {
            lat: 0,
            lon: 0,
            height: 0,
          },
          rotation: [0, 0, 0],
        },
        images: images || [],
      },
      tags: {
        ...(activePointCloud?.id
          ? { [RolodexEntryType.POTREE_POINT_CLOUD]: activePointCloud.id }
          : {}),
        site: site?.id ?? activePointCloud?.tags?.site ?? '',
        organization:
          organization?.id ?? activePointCloud?.tags?.organization ?? '',
      },
    }

    setObservationPosition(null)
    setEditingObservation(null)

    try {
      await rolodexPut([newObservation as RolodexEntryPutJson])

      if (images && images.length > 0) {
        for (let i = 0; i < images.length; i++) {
          const image = images[i]
          if (image.startsWith('http://') || image.startsWith('https://')) {
            continue
          }

          try {
            const response = await fetch(image)
            const blob = await response.blob()

            const mimeType = blob.type
            const extension = mimeType.split('/')[1]
            const file = new File([blob], `image_${i + 1}.${extension}`, {
              type: mimeType,
            })

            await rolodexUploadFile(
              newObservation.id!,
              file,
              `image_${i + 1}.${extension}`
            )
          } catch (error) {
            console.error(
              'Error processing image:',
              error,
              image.substring(0, 100) + '...'
            )
            continue
          }
        }
      }
    } catch (error) {
      console.error('Error saving observation:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
        observation: newObservation,
        imageCount: images?.length || 0,
        site: site?.id,
      })
    } finally {
      await forceRefresh()
    }
  }

  const ignoreObservation = async (observation: GeckoSiteObservation) => {
    const updatedObservation: GeckoSiteObservation = {
      ...observation,
      data: {
        ...observation.data,
        status: undefined,
      },
    }
    await rolodexPatch([updatedObservation])
  }

  const createTicket = async (observation: GeckoSiteObservation) => {
    const id = 'ticket-' + uuid4().slice(0, 8)
    const ticket: Partial<GeckoObservationTicket> = {
      id,
      type: RolodexEntryType.OBSERVATION_TICKET,
      data: {
        id,
        ...observation.data,
        status: TicketStatus.NEW,
      },
      tags: {
        ...observation.tags,
        observation: observation.id,
      },
    }
    await rolodexPut([ticket as RolodexEntryPutJson])
    await forceRefresh()
  }

  return {
    handleEditObservation,
    handleDeleteObservation,
    handleSaveObservation,
    ignoreObservation,
    createTicket,
  }
}
